import type {
  DescMessage,
  DescMethodUnary,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useMutation } from '@connectrpc/connect-query'
import {
  type QueryKey,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'

/**
 * `useInvalidatingMutation` の戻り値型を RPC method (`DescMethodUnary`) から導出する helper。
 *
 * 各 named wrapper hook の戻り値型注釈に使う。 ESLint の
 * `explicit-function-return-type` 規則は exported hook に明示注釈を要求するが、
 * これにより Request / Response の `*Schema` を各 hook で再 import せず、 RPC method
 * 1 つの `typeof` から戻り値型を引ける (= 旧来の冗長な `UseMutationResult<...>` 手書きを回避)。
 */
export type InvalidatingMutationResultFor<M> =
  M extends DescMethodUnary<infer I, infer O>
    ? UseMutationResult<MessageShape<O>, ConnectError, MessageInitShape<I>>
    : never

/**
 * 「成功したら指定の query を invalidate するだけ」 の mutation hook を組み立てる共通 factory。
 *
 * Connect RPC の mutation hook はほぼすべて
 * 「`useMutation(rpc, { onSuccess: invalidate(list[, detail]) })`」 の同一骨格で、
 * 可変なのは (a) どの RPC か (b) どの queryKey を invalidate するか の 2 点だけだった。
 * その骨格をここに 1 本化し、 各 hook は RPC と invalidate 対象の宣言だけを持つ薄い
 * wrapper にする (= named hook は call site と「どの cache を最新化するか」 の業務知識を残す)。
 *
 * invalidate は `Promise.all` で並列実行する (= 旧実装は list → detail を直列 await して
 * いたが、 各 invalidate は独立で直列化する理由が無い)。
 *
 * optimistic update (= `setQueryData` + rollback) や onSuccess の `req` 引数に依存する
 * hook はこの factory の対象外。 個別に `useMutation` を直接書く。
 */
export function useInvalidatingMutation<
  I extends DescMessage,
  O extends DescMessage,
>(
  schema: DescMethodUnary<I, O>,
  invalidateKeys: readonly QueryKey[],
): UseMutationResult<MessageShape<O>, ConnectError, MessageInitShape<I>> {
  const queryClient = useQueryClient()

  return useMutation(schema, {
    onSuccess: () =>
      Promise.all(
        invalidateKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  })
}
